import {
  launchStudioWorkspace,
  redirectToStudio,
} from '../../services/studioService';
import {
  trackStudioLaunchStarted,
  trackStudioLaunchCompleted,
  trackStudioLaunchFailed,
  trackPlatformSwitch,
} from '../../lib/analytics';
import type { CreateGuestSessionResult } from '../../services/authService';

type LaunchContext = {
  isLoggedIn: boolean;
  createGuestSession: (opts?: { email?: string; name?: string }) => Promise<CreateGuestSessionResult>;
  onStep: (step: 'workspace' | 'open') => void;
};

let inFlight: Promise<void> | null = null;

/**
 * Singleton runner so React Strict Mode remounts don't cancel provisioning mid-flight.
 */
export function runStudioLaunchOnce(ctx: LaunchContext): Promise<void> {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    trackStudioLaunchStarted();
    trackPlatformSwitch({ from: 'academy', to: 'studio' });
    if (!ctx.isLoggedIn) {
      ctx.onStep('workspace');
      const guest = await ctx.createGuestSession();
      if (!guest.ok) {
        const message = guest.message || 'Could not create a guest session. Please try again.';
        trackStudioLaunchFailed(message);
        throw new Error(message);
      }
    }

    ctx.onStep('open');
    const launch = await launchStudioWorkspace();
    if (!launch.ok) {
      const message = 'message' in launch ? launch.message : 'Failed to open Studio.';
      trackStudioLaunchFailed(message);
      throw new Error(message);
    }

    trackStudioLaunchCompleted();
    redirectToStudio(launch.handoffToken);
  })().finally(() => {
    inFlight = null;
  });

  return inFlight;
}
